import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { savePdfAs } from "@/create/lecture/lecture-pdf-store";
import { extractPdfText } from "@/create/lecture/extract-pdf-text";
import { uploadStudyDocument } from "@/api/study";
import { Alert, Button, Input, Spinner } from "@/components/ui";

// Standalone Study picker: extract the PDF's text (best-effort), upload it to the
// server (the source of truth for the Study library + marks), warm the local
// IndexedDB blob cache so the page renders instantly, then open the marking
// workspace. Generation stays an opt-in action inside the page.
export function StudyStart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onPick = async (f: File) => {
    setError(null);
    setBusy(true);
    setNote(t("Reading your PDF…"));
    try {
      const name = title.trim() || f.name.replace(/\.pdf$/i, "") || t("Untitled PDF");
      // Best-effort text extraction — scanned/visual-only PDFs still upload and
      // mark from their image snips.
      let extractedText = "";
      try {
        extractedText = await extractPdfText(f, (m) => setNote(m));
      } catch {
        /* no extractable text — the snips still drive marking + generation */
      }
      setNote(t("Saving to your library…"));
      const doc = await uploadStudyDocument({ file: f, filename: name, extractedText });
      // Warm the local blob cache under the server docId so the page renders
      // without re-downloading the PDF.
      try {
        await savePdfAs(doc.id, f);
      } catch {
        /* the page will fall back to fetching doc.pdf_url */
      }
      navigate(`/app/study/${doc.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Couldn't upload that PDF."));
      setBusy(false);
      setNote(null);
    }
  };

  return (
    <div className="mx-auto max-w-2xl animate-fade-up space-y-5">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("Mark & study a PDF")}</h1>
        <p className="mt-1 text-ink-faint">
          {t(
            "Open a lecture or paper, read it, and mark what matters. Snip an equation or figure to get it explained on the spot, or ask Eddi a question — all saved to your library. Turn your marks into a question set whenever you like.",
          )}
        </p>
      </div>

      <div className="card p-5">
        <Input
          label={t("Name (optional)")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("Auto-named from the file if blank")}
        />
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
          }}
        />
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => fileInput.current?.click()} disabled={busy}>
            {t("Choose a PDF")}
          </Button>
          {busy && (
            <span className="flex items-center gap-2 text-sm text-ink-faint">
              <Spinner className="h-4 w-4" /> {note ?? t("Opening…")}
            </span>
          )}
        </div>
      </div>

      {error && <Alert>{error}</Alert>}
    </div>
  );
}
